import { useEffect, useState } from "react";
import { deleteRequestApi, getRideDetails, rideRequestsApi } from "services/auth/rideApis.js";
import { MdCancel } from "react-icons/md";
import { FaArrowRight, FaChevronRight, FaArrowDown } from "react-icons/fa";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import toast from "react-hot-toast";

const colorMap = {
  "pending": "bg-yellow-100/80 text-yellow-900 border border-yellow-300/50",
  "approved": "bg-green-100/80 text-green-900 border border-green-300/50",
  "rejected": "bg-red-100/80 text-red-900 border border-red-300/50"
};

function RequestItem({ setUserRideRequests, request }) {
  const [open, setOpen] = useState(false);
  const [ride, setRide] = useState(null);
  useEffect(() => {
    async function fetchRide() {
      const response = await getRideDetails(request.rideId);
      setRide(response);
    }
    fetchRide();
  }, [request.rideId]);
  // Loading placeholder while ride is null
  if (!ride) {
    return (
      <Card className="mx-auto w-full bg-white/90 backdrop-blur-md border border-white/50 shadow-sm rounded-xl">
        <CardContent className="flex flex-col gap-2 p-5">
          <div className="animate-pulse flex flex-col gap-3">
            <div className="h-6 bg-zinc-200/60 rounded w-1/2" />
            <div className="h-4 bg-zinc-200/60 rounded w-1/3" />
            <div className="h-4 bg-zinc-200/60 rounded w-full" />
            <div className="h-4 bg-zinc-200/60 rounded w-2/3" />
          </div>
        </CardContent>
      </Card>
    );
  }

  async function getRideRequests() {
    const data = await rideRequestsApi();
    setUserRideRequests(data);
  }

  async function handleCancelDelete(requestId) {
    toast.loading("Cancelling the request...");
    const response = await deleteRequestApi(requestId);
    if (response) {
      getRideRequests();
    } else {
      toast.error("Could not cancel the request");
    }
  }
  return (
    <Card className="mx-auto w-full bg-white/90 backdrop-blur-md border border-white/50 shadow-sm hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-1 transition-all duration-300 rounded-xl overflow-hidden min-h-[100px] flex flex-col justify-center">
      <CardContent className="flex flex-col gap-0 p-5 md:p-7">
        <div className="flex w-full flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3 flex-1 min-w-0 w-full pr-4">
            <span className="font-semibold text-lg md:text-xl text-zinc-900 capitalize truncate flex-1 min-w-0" title={ride?.from}>{ride?.from}</span>
            <span className="text-amber-500 flex-shrink-0 hidden md:block">
              <FaArrowRight />
            </span>
            <span className="text-amber-500 flex-shrink-0 md:hidden ml-2">
              <FaArrowDown />
            </span>
            <span className="font-semibold text-lg md:text-xl text-zinc-900 capitalize truncate flex-1 min-w-0" title={ride?.to}>{ride?.to}</span>
          </div>
          
          <div className="flex gap-3 flex-col sm:flex-row md:flex-col lg:flex-row w-full md:w-auto items-start sm:items-center md:items-end flex-shrink-0">
            <div className="flex gap-2 items-center">
              <span className={`py-1.5 px-3 text-xs rounded-md font-semibold capitalize tracking-wide shadow-sm ${colorMap[request.status.toLowerCase()]}`}>
                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
              </span>
              <Button
                variant="destructive"
                size="sm"
                className="cursor-pointer shadow-sm hover:bg-red-600 font-medium h-8"
                onClick={() => handleCancelDelete(request.id)}
              >
                <span className="hidden sm:flex mr-1"><MdCancel size={16} /></span>
                <span>Cancel</span>
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer w-full sm:w-auto flex items-center justify-center gap-2 border-zinc-300/70 text-zinc-700 bg-white/50 hover:bg-white hover:text-zinc-900 shadow-sm transition-all h-8"
              onClick={() => setOpen((prev) => !prev)}
              aria-expanded={open}
            >
              <FaChevronRight className={`transition-transform duration-300 text-amber-500 ${open ? "rotate-90" : ""}`} />
              <span className="font-medium">{open ? "Hide Details" : "View Details"}</span>
            </Button>
          </div>
        </div>
        {/* Animated reveal section */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden w-full`}
          style={{
            maxHeight: open ? 500 : 0,
            opacity: open ? 1 : 0,
            marginTop: open ? 16 : 0,
          }}
        >
          {open && (
            <div className="bg-zinc-50/70 backdrop-blur-sm rounded-lg p-4 border border-zinc-200/50 text-sm sm:text-base flex flex-col gap-4 items-start shadow-inner">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-start items-start">
                <div className="flex-1 flex flex-col items-start">
                  <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Date</div>
                  <div className="text-zinc-900 font-medium">{ride.date ? new Date(ride.date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : "N/A"}</div>
                </div>
                <div className="flex-1 flex flex-col items-start">
                  <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Host</div>
                  <div className="text-zinc-900 font-medium">{ride.host.name}</div>
                </div>
                <div className="flex-1 flex flex-col items-start">
                  <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Capacity</div>
                  <div className="text-zinc-900 font-medium">{ride?.capacity} seats</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-start items-start">
                <div className="flex-1 flex flex-col items-start">
                  <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Per Head Cost</div>
                  <div className="text-zinc-900 font-medium">{ride?.totalFare ? `₹ ${ride?.totalFare}` : "TBD"}</div>
                </div>
                <div className="flex-1 flex flex-col items-start overflow-hidden">
                  <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Vehicle Type</div>
                  <div className="text-zinc-900 font-medium capitalize">{ride?.vehicleType || "car"}</div>
                </div>
                <div className="flex-1 flex flex-col items-start">
                  <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-0.5">Contact</div>
                  {ride.host.phone && request.status.toLowerCase() === "approved" ? (
                    <a
                      href={`https://wa.me/91${ride.host.phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-medium px-3 py-1.5 rounded-md mt-1 transition-colors shadow-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.52 3.48A11.87 11.87 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.16 1.6 5.97L0 24l6.19-1.62A11.93 11.93 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52zM12 22c-1.85 0-3.68-.5-5.25-1.45l-.38-.22-3.68.96.98-3.58-.25-.37A9.93 9.93 0 0 1 2 12c0-5.52 4.48-10 10-10s10 4.48 10 10-4.48 10-10 10zm5.13-7.47c-.28-.14-1.65-.81-1.9-.9-.25-.09-.43-.14-.61.14-.18.28-.7.9-.86 1.08-.16.18-.32.2-.6.07-.28-.14-1.19-.44-2.27-1.41-.84-.75-1.41-1.68-1.58-1.96-.16-.28-.02-.43.12-.57.13-.13.28-.34.42-.51.14-.17.18-.29.28-.48.09-.19.05-.36-.02-.5-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.35-.01-.54-.01-.19 0-.5.07-.76.34-.26.27-1 1-.99 2.43 0 1.43 1.03 2.81 1.18 3 .15.19 2.03 3.1 4.93 4.23.69.3 1.23.48 1.65.61.69.22 1.32.19 1.81.12.55-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33z" />
                      </svg>
                      WhatsApp
                    </a>
                  ) : (
                    <span className="text-zinc-400 italic mt-1 text-sm bg-zinc-100/50 px-2 py-1 rounded">Waiting for approval</span>
                  )}
                </div>
              </div>
              {ride?.description && (
                  <div className="flex flex-col w-full justify-start items-start mt-2">
                    <div className="font-medium text-zinc-500 text-xs uppercase tracking-wider mb-1">Description</div>
                    <div className="text-zinc-800 bg-white/60 p-3 rounded-md border border-white/50 w-full text-sm leading-relaxed shadow-sm">{ride?.description}</div>
                  </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyRequests() {
  const [userRideRequests, setUserRideRequests] = useState([]);

  useEffect(() => {
    async function getRideRequests() {
      toast.loading("Loading your requests...");
      const data = await rideRequestsApi();
      toast.dismiss();
      setUserRideRequests(data);
    }
    getRideRequests();
  }, []);

  return (
    <div className="hideScrollBars overflow-y-auto flex-1 py-8 px-4 backdrop-blur z-10 w-full">
      <div className="max-w-5xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold text-zinc-900 drop-shadow-sm mb-2">My Requests</h1>
        
        <div className="flex flex-col space-y-5">
            {userRideRequests?.length > 0 ? (
                userRideRequests.map((userRequest) => (
                    <RequestItem key={userRequest.id} setUserRideRequests={setUserRideRequests} request={userRequest} />
                ))
            ) : (
                <div className="flex items-center flex-col space-y-4 justify-center text-2xl h-64 w-full">
                    <span className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-sm border border-white/50 text-zinc-800 font-medium">
                        No requests made yet.
                    </span>
                </div>
            )}
        </div>
      </div>
    </div>
  );
}
